import type { Follow } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  InfoButton,
  Label,
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
  useSortedSliderValueInputProps,
} from "@tandemdiabetes/sugarmate-react-components"
import dayjs from "dayjs"
import {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react"

import { useDebounceCallback } from "usehooks-ts"
import styles from "./Ranges.module.scss"
import {
  usePolyglot,
  useFollowedUsers,
} from "src/contexts"
import { useSegment } from "src/contexts/SegmentContext"
import {
  useSliderInputProps,
  useGetGlucoseValue,
  useSetGlucoseValue,
  useGlucoseInputProps,
  useReadableGlucoseUnit,
} from "src/hooks"
import { clamp } from "src/utils"

const polyglotPrefix = "pages.settings.ranges.normal_range."

const inputProps = {
  min: 50,
  max: 400,
  mgdlStep: 5,
}

interface Props {
  updatedAt?: string;
  updateFollow: (settings: Partial<Follow>) => void;
}

const bottomNumberId = "range-normal-bottom-number"
const topNumberId = "range-normal-top-number"

export function NormalRange(props: Props) {
  const {
    updateFollow,
    updatedAt,
  } = props

  const { currentFollow } = useFollowedUsers()

  const polyglot = usePolyglot()

  const displaySettings = currentFollow.display_settings

  const {
    range_normal_bottom: currentRangeBottom,
    range_normal_top: currentRangeTop,
  } = displaySettings

  const editedAtRef = useRef<string>(dayjs().toISOString())

  const readableGlucoseUnit = useReadableGlucoseUnit()
  const getGlucoseValue = useGetGlucoseValue()
  const setGlucoseValue = useSetGlucoseValue()

  const analyticsRef = useSegment()

  const [
    valueOne,
    setValueOne,
  ] = useState<number>(currentRangeBottom)

  const [
    valueTwo,
    setValueTwo,
  ] = useState<number>(currentRangeTop)

  // when the settings property changes, update the state
  useEffect(
    () => {
      if (!updatedAt || dayjs(updatedAt).isAfter(editedAtRef.current)) {
        setValueOne(currentRangeBottom)
        setValueTwo(currentRangeTop)
      }
    },
    [
      editedAtRef,
      currentRangeBottom,
      currentRangeTop,
      updatedAt,
      setValueOne,
      setValueTwo,
    ],
  )

  const trackUpdate = useDebounceCallback(
    useCallback(
      (lower_bound: number, upper_bound: number) => {
        void analyticsRef.current.track(
          "Normal Glucose Range Set",
          {
            "Lower Bound": lower_bound,
            "Upper Bound": upper_bound,
          },
        )
      },
      [analyticsRef],
    ),
    3000,
  )

  // when value one changes, update the state and trigger a settings update
  const onValueOneChange = useCallback(
    (v: number) => {
      // new value in mgdl
      const newValue = clamp(
        setGlucoseValue(v),
        inputProps.min,
        inputProps.max,
      )
      setValueOne(newValue)

      const chartBottom = Math.min(
        newValue,
        valueTwo,
      )
      const chartTop = Math.max(
        newValue,
        valueTwo,
      )

      updateFollow({
        display_settings: {
          ...displaySettings,
          range_normal_bottom: chartBottom,
          range_normal_top: chartTop,
        },
      })

      trackUpdate(
        chartBottom,
        chartTop,
      )

      editedAtRef.current = dayjs().toISOString()
    },
    [
      editedAtRef,
      setValueOne,
      valueTwo,
      updateFollow,
      setGlucoseValue,
      displaySettings,
      trackUpdate,
    ],
  )

  // when value two changes, update the state and trigger a settings update
  const onValueTwoChange = useCallback(
    (v: number) => {
      // new value in mgdl
      const newValue = clamp(
        setGlucoseValue(v),
        inputProps.min,
        inputProps.max,
      )
      setValueTwo(newValue)

      const chartBottom = Math.min(
        newValue,
        valueOne,
      )
      const chartTop = Math.max(
        newValue,
        valueOne,
      )

      updateFollow({
        display_settings: {
          ...displaySettings,
          range_normal_bottom: chartBottom,
          range_normal_top: chartTop,
        },
      })

      trackUpdate(
        chartBottom,
        chartTop,
      )

      editedAtRef.current = dayjs().toISOString()
    },
    [
      setGlucoseValue,
      editedAtRef,
      setValueTwo,
      valueOne,
      updateFollow,
      displaySettings,
      trackUpdate,
    ],
  )

  // shared props for all inputs
  const sharedProps = useGlucoseInputProps(inputProps)

  // the unique props for the first slider input
  const inputOneProps = useSliderInputProps({
    value: getGlucoseValue(valueOne),
    onChange: onValueOneChange,
    updatedAt,
    min: sharedProps.min,
    max: sharedProps.max,
    inputStep: sharedProps.step,
    sliderStep: sharedProps.step,
    toFixed: true,
  })

  // the unique props for the second slider input
  const inputTwoProps = useSliderInputProps({
    value: getGlucoseValue(valueTwo),
    onChange: onValueTwoChange,
    updatedAt,
    min: sharedProps.min,
    max: sharedProps.max,
    inputStep: sharedProps.step,
    sliderStep: sharedProps.step,
    toFixed: true,
  })

  const numberInputOneProps = useMemo(
    () => ({
      value: inputOneProps.inputValue,
      onChange: inputOneProps.onNumberChange,
      onBlur: inputOneProps.onNumberBlur,
      onKeyDown: inputOneProps.onNumberKeyDown,
    }),
    [inputOneProps],
  )

  const numberInputTwoProps = useMemo(
    () => ({
      value: inputTwoProps.inputValue,
      onChange: inputTwoProps.onNumberChange,
      onBlur: inputTwoProps.onNumberBlur,
      onKeyDown: inputTwoProps.onNumberKeyDown,
    }),
    [inputTwoProps],
  )

  const [
    lowInputProps,
    highInputProps,
  ] = useSortedSliderValueInputProps(
    numberInputOneProps,
    numberInputTwoProps,
  )

  return (
    <div>
      <Label id = "normal-range-label">
        {polyglot.t(`${polyglotPrefix}label`)}
        <InfoButton title={polyglot.t(`${polyglotPrefix}title`)}>
          {polyglot.t(`${polyglotPrefix}description`)}
        </InfoButton>
      </Label>
      <div className={styles.inputContainer}>
        <SliderValue
          sizePreset="small"
          alignmentPreset="center"
        >
          <SliderValueInput
            {...sharedProps}
            {...lowInputProps}
            placeholder=""
            id={bottomNumberId}
          />
          <SliderValueLabelContainer>
            <SliderValueLabel htmlFor={bottomNumberId}>
              {readableGlucoseUnit}
            </SliderValueLabel>
          </SliderValueLabelContainer>
        </SliderValue>
        <Slider
          className={styles.rangeInputSlider}
          id="range-normal-slider"
        >
          <SliderInput
            value={inputOneProps.sliderValue}
            onChange={inputOneProps.onSliderChange}
            {...sharedProps}
          />
          <SliderInput
            value={inputTwoProps.sliderValue}
            onChange={inputTwoProps.onSliderChange}
            {...sharedProps}
          />
          <SliderTracks
            min={sharedProps.min}
            max={sharedProps.max}
          />
        </Slider>
        <SliderValue
          sizePreset="small"
          alignmentPreset="center"
        >
          <SliderValueInput
            {...sharedProps}
            {...highInputProps}
            placeholder=""
            id="range-normal-top-number"
          />
          <SliderValueLabelContainer>
            <SliderValueLabel htmlFor={topNumberId}>
              {readableGlucoseUnit}
            </SliderValueLabel>
          </SliderValueLabelContainer>
        </SliderValue>
      </div>
    </div>
  )
}
